import React from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import { v4 as uuid } from 'uuid';
import { sendToSentry } from '../../libs/sentryErrorLogging';
import ErrorText from './ErrorBoundary.style';

/**
 * Wrapper component that catches error thrown in its `children` component and
 * renders a _Something went wrong..._ instead.
 * Can be used on a coarse-grained level around Container components, but also fine-grained, e.g. around every roster day in the roster week overview.
 *
 * In production, this component sends the error with extra data to Sentry.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  componentDidCatch(error, errorInfo) {
    const { children } = this.props;
    let props = [];
    const extraData = {};
    this.setState({ error });
    React.Children.forEach(children, (element) => {
      if (element && Array.isArray(element.props)) props = [...props, ...element.props];
    });
    if (process.env.NODE_ENV === 'production') {
      /* Set the router location property if present */
      if (props.location) extraData.routerLocation = props.location;
      Sentry.withScope((scope) => {
        scope.setExtras(errorInfo);
        scope.setExtras(extraData);
        sendToSentry(error);
      });
    } else {
      /* With the correct webpack devtool, this debugger call should not be necessary.
         However, we keep it here to make sure all errors are paused in the dev tools */
      // eslint-disable-next-line no-debugger
      debugger;
    }
  }

  render() {
    const { error } = this.state;
    const { children } = this.props;
    if (error) {
      /* render fallback UI */
      /* TODO design this + helpdesk link? */
      return (
        <ErrorText key={`errorboundary${uuid()}`}>
          {i18n('Something went wrong...')}
        </ErrorText>
      );
    }
    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired
};

ErrorBoundary.defaultProps = {};
